import { COVER_VOLUME_01, HERO_MOCKUP, IMAGE_ORIGIN } from '../data/woodworking'
import { FACEBOOK_DOMAIN_VERIFICATION } from '../data/analytics'

interface MetaTag {
  /** `name` for standard/Twitter tags, `property` for Open Graph. */
  attr: 'name' | 'property'
  key: string
  content: string
}

interface LinkTag {
  rel: string
  href: string
  as?: string
  crossOrigin?: string
  fetchPriority?: 'high' | 'low' | 'auto'
}

const PAGE_TITLE = 'Woodworking Furniture Plans™ — 60 Furniture Projects in 6 PDF Volumes'
const PAGE_DESCRIPTION =
  'Explore 60 woodworking furniture projects with dimensions in inches and millimeters, cut lists, technical views, and illustrated assembly guidance. Commercial build rights included.'

const META_TAGS: MetaTag[] = [
  { attr: 'name', key: 'description', content: PAGE_DESCRIPTION },
  { attr: 'property', key: 'og:type', content: 'product' },
  { attr: 'property', key: 'og:site_name', content: 'Woodworking Furniture Plans™' },
  { attr: 'property', key: 'og:title', content: PAGE_TITLE },
  { attr: 'property', key: 'og:description', content: PAGE_DESCRIPTION },
  { attr: 'property', key: 'og:image', content: COVER_VOLUME_01.src },
  { attr: 'property', key: 'og:image:alt', content: COVER_VOLUME_01.alt },
  { attr: 'property', key: 'og:locale', content: 'en_US' },
  { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
  { attr: 'name', key: 'twitter:title', content: PAGE_TITLE },
  { attr: 'name', key: 'twitter:description', content: PAGE_DESCRIPTION },
  { attr: 'name', key: 'twitter:image', content: COVER_VOLUME_01.src },
  ...(FACEBOOK_DOMAIN_VERIFICATION
    ? [{ attr: 'name' as const, key: 'facebook-domain-verification', content: FACEBOOK_DOMAIN_VERIFICATION }]
    : []),
]

const LINK_TAGS: LinkTag[] = [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'preconnect', href: IMAGE_ORIGIN },
  // The hero mockup is the LCP element.
  { rel: 'preload', href: HERO_MOCKUP.src, as: 'image', fetchPriority: 'high' },
]

/** Inserts global <meta> and resource-hint <link> tags into <head> once. */
export function initHeadMeta(): void {
  if (typeof document === 'undefined') return

  document.title = PAGE_TITLE
  document.documentElement.lang = 'en-US'
  // Remove any stale verification tag or structured data from the previous product.
  if (!FACEBOOK_DOMAIN_VERIFICATION) {
    document.querySelector('meta[name="facebook-domain-verification"]')?.remove()
  }
  document.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove())

  META_TAGS.forEach(({ attr, key, content }) => {
    const existing = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
    if (existing) {
      existing.content = content
      return
    }
    const meta = document.createElement('meta')
    meta.setAttribute(attr, key)
    meta.content = content
    document.head.appendChild(meta)
  })

  LINK_TAGS.forEach(({ rel, href, as, crossOrigin, fetchPriority }) => {
    if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return

    const link = document.createElement('link')
    link.rel = rel
    link.href = href
    if (as) link.as = as
    if (crossOrigin) link.crossOrigin = crossOrigin
    if (fetchPriority) link.setAttribute('fetchpriority', fetchPriority)
    document.head.appendChild(link)
  })
}
